import Phaser from 'phaser';
import { Key9Scene } from './game/Key9Scene';
import type { Key9Puzzle, Key9Result } from './types/puzzle';
import { validatePuzzleShape, resultLabel } from './logic/key9Validator';
import {
  loadSave,
  saveFullDone,
  saveFullProgress,
  saveKey9Missed,
  saveKey9Unlock,
  type DayProgress
} from './storage/localSave';
import { buildFullShareText, buildShareText, formatTime } from './logic/share';
import {
  getInitialDayFlow,
  getInlineKey9Actions,
  getMissedPanelCopy,
  getResultActionSheet,
  type InlineKey9ActionState,
  type ResultActionSheetState
} from './logic/dayFlow';

let game: Phaser.Game | null = null;
let currentPuzzle: Key9Puzzle | null = null;
let timerId: number | null = null;
let startMs = Date.now();
let latestKey9Result: Key9Result | null = null;
let latestProgress: DayProgress | null = null;
let lastMisses = 0;
let buttonsBound = false;
let eventsBound = false;

const containerId = 'daysudoku-game';

async function boot() {
  const container = document.getElementById(containerId);
  if (!container) return;
  bindButtons();
  bindEvents();

  currentPuzzle = await loadTodayPuzzle();
  const errors = validatePuzzleShape(currentPuzzle);
  if (errors.length) {
    console.error('Invalid puzzle:', errors);
    container.textContent = 'Puzzle data error.';
    return;
  }

  const save = loadSave();
  const progress = save.days[currentPuzzle.date] ?? null;
  latestProgress = progress;
  latestKey9Result = progress?.key9Result ?? save.results[currentPuzzle.date] ?? null;

  if (progress?.phase === 'full_done') {
    showFullDone(progress, save.currentStreak);
    return;
  }

  const flow = getInitialDayFlow(progress);
  if (flow.mode == null) return;
  startMs = Date.now();
  game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: containerId,
    width: 430,
    height: 630,
    backgroundColor: '#f5f7fb',
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH
    },
    scene: [Key9Scene]
  });
  game.scene.start('Key9Scene', { puzzle: currentPuzzle, mode: flow.mode, progress: progress ?? undefined, practice: flow.practice });
  setStatusLabel(flow.statusLabel);

  if (flow.panel === 'missed' && latestKey9Result) {
    showKey9Missed(latestKey9Result);
    stopTimer();
    setMisses(latestKey9Result.misses);
    return;
  }

  startTimer();
}

async function loadTodayPuzzle(): Promise<Key9Puzzle> {
  const today = localDateKey();
  const year = today.slice(0, 4);
  const urls = [`/puzzles/${year}/${today}.json`, '/puzzles/2026/2026-06-12.json'];
  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: 'no-cache' });
      if (res.ok) return await res.json() as Key9Puzzle;
    } catch {
      // try next fallback
    }
  }
  throw new Error('No puzzle found');
}

function localDateKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function bindButtons() {
  if (buttonsBound) return;
  buttonsBound = true;
  document.getElementById('ds-undo')?.addEventListener('click', () => getScene()?.undo());
  document.getElementById('ds-erase')?.addEventListener('click', () => getScene()?.erase());
  document.getElementById('ds-hint')?.addEventListener('click', () => getScene()?.hint());
  document.getElementById('ds-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-sheet-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-inline-share')?.addEventListener('click', shareLatest);
  document.getElementById('ds-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-sheet-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-inline-practice')?.addEventListener('click', startKey9Practice);
  document.getElementById('ds-continue')?.addEventListener('click', continueSudoku);
  document.getElementById('ds-sheet-continue')?.addEventListener('click', continueSudoku);
  document.getElementById('ds-inline-continue')?.addEventListener('click', continueSudoku);
}

function bindEvents() {
  if (eventsBound) return;
  eventsBound = true;
  window.addEventListener('daysudoku:key9-status', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { misses: number; phase?: string };
    const increased = detail.misses > lastMisses;
    setMisses(detail.misses);
    if (increased) {
      const missEl = document.getElementById('ds-misses');
      missEl?.classList.remove('miss-pop');
      window.setTimeout(() => missEl?.classList.add('miss-pop'), 0);
    }
    lastMisses = detail.misses;
    if (detail.phase === 'full_in_progress') setStatusLabel('Continue today’s Sudoku');
    if (detail.phase === 'key9_practice') setStatusLabel('Key9 Practice');
  }) as EventListener);

  window.addEventListener('daysudoku:key9-unlocked', ((event: Event) => onKey9Unlocked((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-missed', ((event: Event) => onKey9Missed((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-practice-unlocked', ((event: Event) => onKey9PracticeUnlocked((event as CustomEvent).detail as Key9Result)) as EventListener);
  window.addEventListener('daysudoku:key9-practice-missed', (() => {
    if (latestKey9Result) showKey9Missed(latestKey9Result);
    stopTimer();
  }) as EventListener);
  window.addEventListener('daysudoku:full-progress', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { board: string[] };
    if (!currentPuzzle) return;
    const saved = saveFullProgress(currentPuzzle.date, detail.board);
    latestProgress = saved.days[currentPuzzle.date];
  }) as EventListener);
  window.addEventListener('daysudoku:full-complete', ((event: Event) => {
    const detail = (event as CustomEvent).detail as { board: string[]; seconds: number };
    onFullComplete(detail.board, detail.seconds);
  }) as EventListener);
}

function getScene(): Key9Scene | null {
  if (!game) return null;
  return game.scene.getScene('Key9Scene') as Key9Scene;
}

function startTimer() {
  if (timerId) window.clearInterval(timerId);
  timerId = window.setInterval(() => {
    const seconds = Math.round((Date.now() - startMs) / 1000);
    const el = document.getElementById('ds-timer');
    if (el) el.textContent = formatTime(seconds);
  }, 1000);
}

function stopTimer() {
  if (timerId) window.clearInterval(timerId);
  timerId = null;
}

function setMisses(misses: number) {
  const missEl = document.getElementById('ds-misses');
  if (missEl) missEl.textContent = `Misses ${misses}/9`;
}

function startKey9Practice() {
  hideResultPanel();
  getScene()?.retryKey9Practice();
  startMs = Date.now();
  startTimer();
  setStatusLabel('Key9 Practice');
  setMisses(0);
}

function continueSudoku() {
  hideResultPanel();
  getScene()?.continueSudoku();
  startMs = Date.now();
  startTimer();
  setStatusLabel('Continue today’s Sudoku');
}

function onKey9Unlocked(result: Key9Result) {
  stopTimer();
  latestKey9Result = result;
  const saved = currentPuzzle ? saveKey9Unlock(currentPuzzle.date, result) : loadSave();
  latestProgress = currentPuzzle ? saved.days[currentPuzzle.date] : null;
  showKey9Unlocked(result);
}

function onKey9Missed(result: Key9Result) {
  stopTimer();
  latestKey9Result = result;
  const saved = currentPuzzle ? saveKey9Missed(currentPuzzle.date, result) : loadSave();
  latestProgress = currentPuzzle ? saved.days[currentPuzzle.date] : null;
  showKey9Missed(result);
}

function onKey9PracticeUnlocked(result: Key9Result) {
  stopTimer();
  showPanel({
    title: 'Key9 Practice Complete',
    summary: `Practice run complete · Misses ${result.misses}/9 · ${formatTime(result.seconds)}`,
    status: 'Key9 Practice',
    doneCopy: '',
    inlineState: 'key9_practice_complete',
    sheetState: 'key9_missed',
    showPractice: true,
    practiceText: 'Try Again',
    showContinue: true,
    continueText: 'Continue Sudoku',
    shareText: 'Share Result'
  });
}

function onFullComplete(board: string[], seconds: number) {
  stopTimer();
  if (!currentPuzzle) return;
  const saved = saveFullDone(currentPuzzle.date, board, seconds);
  latestProgress = saved.days[currentPuzzle.date];
  showFullDone(latestProgress, saved.currentStreak);
}

function showKey9Unlocked(result: Key9Result) {
  showPanel({
    title: 'Key9 Unlocked',
    summary: 'You placed 1–9. Now finish the Sudoku.',
    status: 'Key9 Unlocked',
    doneCopy: '',
    inlineState: 'key9_unlocked',
    sheetState: 'key9_unlocked',
    showPractice: false,
    practiceText: 'Try Again',
    showContinue: true,
    continueText: 'Continue Sudoku',
    shareText: 'Share Key9'
  });
}

function showKey9Missed(result: Key9Result) {
  const copy = getMissedPanelCopy();
  showPanel({
    title: copy.title,
    summary: copy.body,
    status: 'Key9 Missed',
    doneCopy: '',
    inlineState: 'key9_missed',
    sheetState: 'key9_missed',
    showPractice: true,
    practiceText: copy.primaryButton,
    showContinue: true,
    continueText: copy.secondaryButton,
    shareText: copy.tertiaryButton
  });
}

function showFullDone(progress: DayProgress, streak?: number) {
  const key9 = progress.key9Result;
  if (key9?.status === 'missed') {
    showPanel({
      title: 'Today’s Sudoku completed.',
      summary: `Key9 result: Missed. Full Sudoku ${formatTime(progress.fullSeconds ?? 0)}${streak && streak > 1 ? ` · ${streak}-day streak` : ''}`,
      status: 'Sudoku completed',
      doneCopy: 'You’re done for today.',
      inlineState: 'full_done',
      sheetState: 'full_done',
      showPractice: false,
      practiceText: 'Try Again',
      showContinue: false,
      continueText: 'Continue Sudoku',
      shareText: 'Share Result'
    });
    return;
  }

  const key9Copy = key9 ? `Key9 ${resultLabel(key9.status)} · Misses ${key9.misses}/9` : 'Key9 unlocked';
  showPanel({
    title: 'Today’s DaySudoku is done.',
    summary: `${key9Copy} · Full Sudoku ${formatTime(progress.fullSeconds ?? 0)}${streak && streak > 1 ? ` · ${streak}-day streak` : ''}`,
    status: 'Done for today',
    doneCopy: 'You’re done for today.',
    inlineState: 'full_done',
    sheetState: 'full_done',
    showPractice: false,
    practiceText: 'Try Again',
    showContinue: false,
    continueText: 'Continue Sudoku',
    shareText: 'Share Result'
  });
}

function showPanel(config: {
  title: string;
  summary: string;
  status: string;
  doneCopy: string;
  inlineState: InlineKey9ActionState;
  sheetState: ResultActionSheetState;
  showPractice: boolean;
  practiceText: string;
  showContinue: boolean;
  continueText: string;
  shareText: string;
}) {
  const panel = document.getElementById('ds-result');
  const card = document.querySelector('[data-game-card]');
  const title = document.getElementById('ds-result-title');
  const summary = document.getElementById('ds-result-summary');
  const doneCopy = document.getElementById('ds-done-copy');
  const practiceButton = document.getElementById('ds-practice') as HTMLButtonElement | null;
  const continueButton = document.getElementById('ds-continue') as HTMLButtonElement | null;
  const shareButton = document.getElementById('ds-share') as HTMLButtonElement | null;
  const inlineStrip = document.getElementById('ds-key9-actions');
  const inlineStatus = document.getElementById('ds-key9-actions-status');
  const inlinePracticeButton = document.getElementById('ds-inline-practice') as HTMLButtonElement | null;
  const inlineContinueButton = document.getElementById('ds-inline-continue') as HTMLButtonElement | null;
  const inlineShareButton = document.getElementById('ds-inline-share') as HTMLButtonElement | null;
  const sheet = document.getElementById('ds-action-sheet');
  const sheetTitle = document.getElementById('ds-sheet-title');
  const sheetSummary = document.getElementById('ds-sheet-summary');
  const sheetDoneCopy = document.getElementById('ds-sheet-done-copy');
  const sheetPracticeButton = document.getElementById('ds-sheet-practice') as HTMLButtonElement | null;
  const sheetContinueButton = document.getElementById('ds-sheet-continue') as HTMLButtonElement | null;
  const sheetShareButton = document.getElementById('ds-sheet-share') as HTMLButtonElement | null;
  if (title) title.textContent = config.title;
  if (summary) summary.textContent = config.summary;
  if (doneCopy) {
    doneCopy.textContent = config.doneCopy;
    doneCopy.hidden = config.doneCopy.length === 0;
  }
  if (practiceButton) {
    practiceButton.textContent = config.practiceText;
    practiceButton.hidden = !config.showPractice;
  }
  if (continueButton) {
    continueButton.textContent = config.continueText;
    continueButton.hidden = !config.showContinue;
  }
  if (shareButton) shareButton.textContent = config.shareText;

  const inlineConfig = getInlineKey9Actions(config.inlineState);
  const inlineActionCount = Object.keys(inlineConfig.buttons).length;
  if (inlineStrip) {
    inlineStrip.hidden = !inlineConfig.visible;
    inlineStrip.dataset.state = inlineConfig.variant;
    inlineStrip.dataset.actionCount = String(inlineActionCount);
  }
  if (inlineStatus) inlineStatus.textContent = inlineConfig.status;
  if (inlinePracticeButton) {
    inlinePracticeButton.textContent = inlineConfig.buttons.practice ?? config.practiceText;
    inlinePracticeButton.hidden = !inlineConfig.buttons.practice;
    inlinePracticeButton.className = 'key9-primary';
  }
  if (inlineContinueButton) {
    inlineContinueButton.textContent = inlineConfig.buttons.continue ?? config.continueText;
    inlineContinueButton.hidden = !inlineConfig.buttons.continue;
    inlineContinueButton.className = inlineConfig.buttons.practice ? 'key9-secondary' : 'key9-primary';
  }
  if (inlineShareButton) {
    inlineShareButton.textContent = inlineConfig.buttons.share ?? config.shareText;
    inlineShareButton.hidden = !inlineConfig.buttons.share;
    inlineShareButton.className = inlineConfig.buttons.practice ? 'key9-tertiary' : 'key9-secondary';
  }
  panel?.toggleAttribute('data-inline-actions', inlineConfig.visible);

  if (sheetTitle) sheetTitle.textContent = config.title;
  if (sheetSummary) sheetSummary.textContent = config.sheetState === 'full_done' ? 'You’re done for today.' : config.summary;
  if (sheetDoneCopy) {
    sheetDoneCopy.textContent = config.sheetState === 'full_done' && config.summary.includes('Key9 result: Missed') ? 'Key9 result: Missed.' : '';
    sheetDoneCopy.hidden = sheetDoneCopy.textContent.length === 0;
  }

  const sheetConfig = getResultActionSheet(config.sheetState);
  const actionCount = Object.keys(sheetConfig.buttons).length;
  if (sheet) {
    sheet.hidden = !sheetConfig.visible;
    sheet.dataset.state = sheetConfig.variant;
    sheet.dataset.actionCount = String(actionCount);
  }
  card?.classList.toggle('sheet-open', sheetConfig.visible);
  if (sheetPracticeButton) {
    sheetPracticeButton.textContent = sheetConfig.buttons.practice ?? config.practiceText;
    sheetPracticeButton.hidden = !sheetConfig.buttons.practice;
  }
  if (sheetContinueButton) {
    sheetContinueButton.textContent = sheetConfig.buttons.continue ?? config.continueText;
    sheetContinueButton.hidden = !sheetConfig.buttons.continue;
  }
  if (sheetShareButton) {
    sheetShareButton.textContent = sheetConfig.buttons.share ?? config.shareText;
    sheetShareButton.hidden = !sheetConfig.buttons.share;
  }
  setStatusLabel(config.status);
  if (panel) panel.hidden = false;
}

function hideResultPanel() {
  const panel = document.getElementById('ds-result');
  const inlineStrip = document.getElementById('ds-key9-actions');
  const sheet = document.getElementById('ds-action-sheet');
  const card = document.querySelector('[data-game-card]');
  if (panel) panel.hidden = true;
  panel?.removeAttribute('data-inline-actions');
  if (inlineStrip) inlineStrip.hidden = true;
  if (sheet) sheet.hidden = true;
  card?.classList.remove('sheet-open');
}

function setStatusLabel(label: string) {
  const statusLabel = document.getElementById('ds-status-label');
  if (statusLabel) statusLabel.textContent = label;
}

async function shareLatest() {
  if (!currentPuzzle) return;
  const save = loadSave();
  const text = latestProgress?.phase === 'full_done'
    ? buildFullShareText(currentPuzzle.date, latestProgress, save.currentStreak)
    : latestKey9Result
      ? buildShareText(currentPuzzle.date, latestKey9Result, save.currentStreak)
      : '';
  if (!text) return;
  if (navigator.share) {
    await navigator.share({ text }).catch(() => undefined);
    return;
  }
  await navigator.clipboard?.writeText(text).catch(() => undefined);
  alert('Result copied to clipboard.');
}

boot();
